import React, { useEffect, useRef, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import DescriptionIcon from '@mui/icons-material/Description';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';

import {
  AddButtonUi,
  AutoCompleteSelectUI,
  ButtonUI,
  CheckboxUI,
  DatePickerUI,
  FormErrorUI,
  FormLabelUI,
  InputUI,
} from '../Interface';
import companyService from '../../service/company.service';
import { generateAutocompleteItems } from '../../utils/helper';
import useConfig from '../../context/ConfigContext';
import { DocumentDirection } from '../../config/DocumentDirection';
import { DocStatus, getDocStatusLabel, getDocStatusList, InvoiceModificationScope } from '../../config/document';
import exchangeRateService from '../../service/exchangeRate.service';
import { IconButton } from '@mui/material';
import { DocumentType } from '../../config/DocumentType';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { getProjectOrderNumberName } from '../../utils/getProjectOrderNumberName';
import useParam from '../../context/ParamContext';
import { FromType, PaymentDeadlineBasisType } from '../../config/paymentMethod';
import dayjs from 'dayjs';
import { calculatePeriodicalDeliveryDate } from '../../utils/periodicalSettlement';
import { getDocumentDateLabels } from '../../utils/documentDateLabels';
import { isDigitalSigningConfigured, isDocumentSigningEnabled } from '../../utils/digitalSigning';

const DocumentFields = ({
  viewField,
  autocompleteOptions,
  documentSeriesOptions,
  selectedValues,
  autocompleteData,
  documentData,
  selectedCompanyState,
  companyContactsState,
  setSettlementDialogOpen,
  docTypeId,
  getDownPaymentDoc,
  currentDocType,
  onCompanyAddOpen,
  onCompanyViewOpen,
  onProjectViewOpen,
  companyBankRefreshKey,
  setBulkUpdateDialogState,
  setProjectDialogOpen,
  setCostCenterDialogOpen,
  issuedInvoiceProjectFieldsEditable = false,
}) => {
  const { params } = useParam();
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const { config, getConfig } = useConfig();
  const { selectedCompany, setSelectedCompany } = selectedCompanyState;
  const isElectronicInvoiceType = [
    DocumentType.DownPayment,
    DocumentType.ARInvoice,
    DocumentType.ARInvoiceModification,
  ].includes(Number(docTypeId));
  const usesElectronicInvoiceMethod =
    isElectronicInvoiceType && selectedCompany?.id === values.companyId && selectedCompany?.electronicInvoiceEnabled;
  const { setCompanyContacts } = companyContactsState;
  const isCopyMode = selectedValues?.mode === 'copy';
  const shouldRefreshCopiedExchangeRate = isCopyMode && selectedValues?.refreshExchangeRateAfterCopy === true;
  const isInvoiceModification = Number(docTypeId) === DocumentType.ARInvoiceModification;
  const isEditableInvoice = [DocumentType.ARInvoiceManual, DocumentType.APInvoice].includes(Number(docTypeId));
  const allowsInvoiceHeaderModification =
    isInvoiceModification &&
    [InvoiceModificationScope.HEAD, InvoiceModificationScope.ALL].includes(values.modificationScope);
  const supportsPeriodicalSettlement = [
    DocumentType.ARInvoiceManual,
    DocumentType.ARInvoice,
    DocumentType.ARInvoiceModification,
  ].includes(Number(docTypeId));
  const periodicalFieldsDisabled =
    viewField ||
    (!isEditableInvoice && Boolean(selectedValues?.finalizedAt)) ||
    (isInvoiceModification && !allowsInvoiceHeaderModification);
  const projectFieldsDisabled = viewField && !issuedInvoiceProjectFieldsEditable;
  const manualDocNum = Boolean(
    documentSeriesOptions?.find((option) => Number(option.value) === Number(values.documentSeriesId))?.manualDocNum
  );
  const hasSelectedValues = Boolean(selectedValues && Object.keys(selectedValues).length > 0);
  const isLoadedExistingDoc = hasSelectedValues && !isCopyMode;
  const isLoadedCompanyDataForCurrentDoc =
    isLoadedExistingDoc &&
    Boolean(values?.docId) &&
    values?.companyId === selectedValues?.companyId &&
    selectedCompany?.id === selectedValues?.companyId;
  const initialLoadedKeyRef = useRef(null);
  const initialLoadSkippedRef = useRef(false);
  const lastHandledKeyRef = useRef(null);
  const lastHandledConfigCurrencyRef = useRef(null);
  const copyCurrencyCodeRef = useRef(null);
  const copyExchangeRateRef = useRef(null);
  const copyPriceListIdRef = useRef(null);
  const preservedCopyRateAppliedRef = useRef(false);
  const companyBankRequestIdRef = useRef(0);
  const latestCompanyBankIdRef = useRef(values.companyBankId);
  const sellerBankStateRef = useRef({ documentKey: null, currencyCode: null });
  const [companyBankRows, setCompanyBankRows] = useState([]);
  const showCompanyFields = Boolean(currentDocType?.company || currentDocType?.cooperation);
  const companyLabel = currentDocType?.cooperation
    ? 'Partner'
    : documentData.direction === DocumentDirection.INCOMING
      ? 'Szállító'
      : 'Vevő';

  const isInventoryAccountingDoc = Boolean(documentData?.inventoryAccounting);
  const dateLabels = getDocumentDateLabels(currentDocType);
  const setCompanyFromProject =
    params.DOCUMENT_SET_COMPANY_FROM_PROJECT === 'Y' &&
    Boolean(currentDocType) &&
    !currentDocType?.invoice &&
    documentData.direction === DocumentDirection.OUTGOING;
  const isNewDocument = !values?.docId && (!selectedValues || Object.keys(selectedValues).length === 0);
  const isDocDateLocked =
    !isEditableInvoice &&
    (documentData.direction === DocumentDirection.OUTGOING
      ? Number(docTypeId) !== DocumentType.Delivery && !isCopyMode
      : isInventoryAccountingDoc) &&
    !isNewDocument &&
    values?.docStatus !== DocStatus.PLANNED;
  const requiresExchangeRate = Boolean(
    config?.currencyCode && values?.currencyCode && values.currencyCode !== config.currencyCode
  );
  const selectedCustomStatusLabel =
    (!isCopyMode ? selectedValues?.customDocStatus?.name : '') ||
    autocompleteData?.customStatus?.find((status) => status.code === values.customStatus)?.name ||
    '';
  const selectedPriorityLabel =
    (!isCopyMode ? selectedValues?.documentPiority?.name : '') ||
    autocompleteData?.documentPiority?.find((priority) => priority.priorityId === values.priorityId)?.name ||
    '';
  const companyBankOptions = generateAutocompleteItems(
    companyBankRows,
    ['bankAccountNumber', 'iban', 'currencyCode'],
    'id'
  );
  const selectedCompanyBank = companyBankRows.find((bank) => bank.id === values.companyBankId);
  const selectedCompanyBankLabel =
    [selectedCompanyBank?.bankAccountNumber, selectedCompanyBank?.iban, selectedCompanyBank?.currencyCode]
      .filter(Boolean)
      .join(' - ') ||
    (values.companyBankId
      ? [
          selectedValues?.companyBank?.bankAccountNumber,
          selectedValues?.companyBank?.iban,
          selectedValues?.companyBank?.currencyCode,
        ]
          .filter(Boolean)
          .join(' - ')
      : '');
  const hidedFieldNames = JSON.parse(params.DOCUMENT_HIDE_FIELDS ?? '{}');

  const getCompanyData = async (companyId) => {
    const selCom = await companyService.getCompanyById(companyId, true);
    setSelectedCompany(selCom);
  };

  const getDailyRate = async () => {
    const rateFilter = { currencyCode: values.currencyCode, rateDate: values.docDate };
    setFieldValue('exchangeRate', '');
    const rate = (await exchangeRateService.getDailyRate(rateFilter)) ?? null;
    if (rate) {
      setFieldValue('exchangeRate', parseFloat(rate.rate));
    }
  };

  useEffect(() => {
    if (!Object.keys(config).length) getConfig();
  }, []);

  useEffect(() => {
    if (
      !viewField &&
      !isInvoiceModification &&
      values.docStatus !== DocStatus.CANCELLATION &&
      (!isLoadedExistingDoc || selectedValues?.docStatus === DocStatus.PLANNED) &&
      usesElectronicInvoiceMethod &&
      !values.electronicSignatureRequested
    ) {
      setFieldValue('electronicSignatureRequested', true, false);
    }
  }, [
    viewField,
    isInvoiceModification,
    values.docStatus,
    isLoadedExistingDoc,
    selectedValues?.docStatus,
    usesElectronicInvoiceMethod,
    values.electronicSignatureRequested,
    setFieldValue,
  ]);

  latestCompanyBankIdRef.current = values.companyBankId;

  useEffect(() => {
    const requestId = ++companyBankRequestIdRef.current;
    let active = true;

    if (!currentDocType?.useBankAccount) {
      setCompanyBankRows([]);
      return () => {
        active = false;
      };
    }

    if (!values.companyId || !values.currencyCode) {
      setCompanyBankRows([]);
      latestCompanyBankIdRef.current = null;
      setFieldValue('companyBankId', null);
      return () => {
        active = false;
      };
    }

    companyService
      .getCompanyBanks(values.companyId, values.currencyCode)
      .then((data) => {
        if (!active || requestId !== companyBankRequestIdRef.current) return;

        const rows = data?.rows ?? [];
        const currentBankId = latestCompanyBankIdRef.current;
        const currentBankIsValid = rows.some((bank) => bank.id === currentBankId);
        const nextBankId = currentBankIsValid ? currentBankId : data?.defaultBankAccount?.id ?? null;

        setCompanyBankRows(rows);
        latestCompanyBankIdRef.current = nextBankId;
        if (nextBankId !== currentBankId) {
          setFieldValue('companyBankId', nextBankId);
        }
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [currentDocType?.useBankAccount, values.companyId, values.currencyCode, companyBankRefreshKey, setFieldValue]);

  useEffect(() => {
    if (!hasSelectedValues) {
      initialLoadedKeyRef.current = null;
      initialLoadSkippedRef.current = false;
      lastHandledKeyRef.current = null;
      lastHandledConfigCurrencyRef.current = null;
      copyCurrencyCodeRef.current = null;
      copyExchangeRateRef.current = null;
      copyPriceListIdRef.current = null;
      preservedCopyRateAppliedRef.current = false;
      return;
    }

    initialLoadedKeyRef.current = `${selectedValues?.currencyCode ?? ''}|${
      selectedValues?.docDate ? dayjs(selectedValues.docDate).format('YYYY-MM-DD') : ''
    }`;
    initialLoadSkippedRef.current = false;
    lastHandledKeyRef.current = null;
    lastHandledConfigCurrencyRef.current = null;
    copyCurrencyCodeRef.current = selectedValues?.mode === 'copy' ? selectedValues?.currencyCode ?? null : null;
    copyExchangeRateRef.current =
      selectedValues?.mode === 'copy' && !shouldRefreshCopiedExchangeRate
        ? selectedValues?.exchangeRate ?? null
        : null;
    copyPriceListIdRef.current = selectedValues?.mode === 'copy' ? selectedValues?.priceListId ?? null : null;
    preservedCopyRateAppliedRef.current = false;
  }, [
    hasSelectedValues,
    selectedValues?.docId,
    selectedValues?.mode,
    selectedValues?.currencyCode,
    selectedValues?.docDate,
    selectedValues?.exchangeRate,
    selectedValues?.priceListId,
    shouldRefreshCopiedExchangeRate,
  ]);

  useEffect(() => {
    if (values.companyId) {
      const matchedCompany =
        autocompleteData?.company?.find((i) => i.id === values.companyId) ??
        (selectedValues?.company?.id === values.companyId ? selectedValues.company : null);

      setFieldValue(
        'companyCode',
        isInvoiceModification ? selectedValues?.companyCode ?? '' : matchedCompany?.code ?? selectedValues?.companyCode ?? ''
      );
      setFieldValue(
        'companyName',
        isInvoiceModification ? selectedValues?.companyName ?? '' : matchedCompany?.name ?? selectedValues?.companyName ?? ''
      );

      const hasLoadedCompanyDetails =
        selectedCompany?.id === values.companyId &&
        Array.isArray(selectedCompany?.companyAdress) &&
        Array.isArray(selectedCompany?.companyContact);

      if (!hasLoadedCompanyDetails) {
        getCompanyData(values.companyId);
      }
    } else {
      setSelectedCompany(null);
      setCompanyContacts([]);
    }
  }, [
    values.companyId,
    autocompleteData?.company,
    selectedValues?.company,
    selectedValues?.companyCode,
    selectedValues?.companyName,
    isInvoiceModification,
  ]);

  useEffect(() => {
    if (Object.keys(values).length === 0) return;
    if (!selectedCompany) {
      if (values.companyId) return;

      const findCurrencyByDefPl = autocompleteData?.priceList?.find((p) => p.id === config.defaultPriceList);

      setFieldValue('companyCode', '');
      setFieldValue('companyName', '');
      setFieldValue('companyContactId', null);
      setFieldValue('companyBankId', null);
      setFieldValue('currencyCode', findCurrencyByDefPl?.currencyCode || config.currencyCode || null);
      setFieldValue('paymentMethodId', config.defPaymentMethod ?? null);
      setFieldValue('salesOrPurchasePersonId', null);
      setFieldValue('deliveryMethodId', null);
      setFieldValue('incotermCode', null);
      setFieldValue('priceListId', config.defaultPriceList ?? null);
      setFieldValue('langCode', null);
      setFieldValue('hunTaxNumber', '');
      setFieldValue('euTaxNumber', '');
      setFieldValue('foreignTaxNumber', '');
      setFieldValue('vatGroupNumber', '');
      setFieldValue('billingAdressId', null);
      setFieldValue('billingCountryCode', null);
      setFieldValue('billingCountry', '');
      setFieldValue('billingProvince', '');
      setFieldValue('billingPostCode', '');
      setFieldValue('billingCity', '');
      setFieldValue('billingStreetAddress', '');
      setFieldValue('shippingAdressId', null);
      setFieldValue('shippingCountryCode', null);
      setFieldValue('shippingCountry', '');
      setFieldValue('shippingProvince', '');
      setFieldValue('shippingPostCode', '');
      setFieldValue('shippingCity', '');
      setFieldValue('shippingStreetAddress', '');
      setFieldValue('shortName', '');
      setFieldValue('incotermCity', '');
      setFieldValue('companyWarning', '');
      return;
    }

    if (selectedCompany.id !== values.companyId) return;

    setCompanyContacts(generateAutocompleteItems(selectedCompany.companyContact, 'name', 'id'));

    if (!isLoadedCompanyDataForCurrentDoc && !isInvoiceModification) {
      const preservedCurrencyCode = isCopyMode ? copyCurrencyCodeRef.current : null;
      const preservedExchangeRate = isCopyMode ? copyExchangeRateRef.current : null;
      const preservedPriceListId = isCopyMode ? copyPriceListIdRef.current : null;
      const selectedContact = selectedCompany.companyContact.find(
        (contact) => Number(contact.id) === Number(values.companyContactId)
      );
      const defContact = selectedCompany.companyContact.find((cc) => cc.default);
      setFieldValue(
        'companyContactId',
        selectedContact?.id ?? defContact?.id ?? selectedCompany?.companyContact[0]?.id ?? null
      );

      setFieldValue('langCode', selectedCompany.languageCode);
      setFieldValue('hunTaxNumber', selectedCompany.hunTaxNumber ?? '');
      setFieldValue('euTaxNumber', selectedCompany.euTaxNumber ?? '');
      setFieldValue('foreignTaxNumber', selectedCompany.foreignTaxNumber ?? '');
      setFieldValue('vatGroupNumber', selectedCompany.vatGroupNumber ?? '');
      setFieldValue('billingAdressId', null);
      setFieldValue('billingCountryCode', null);
      setFieldValue('billingCountry', '');
      setFieldValue('billingProvince', '');
      setFieldValue('billingPostCode', '');
      setFieldValue('billingCity', '');
      setFieldValue('billingStreetAddress', '');
      setFieldValue('shippingAdressId', null);
      setFieldValue('shippingCountryCode', null);
      setFieldValue('shippingCountry', '');
      setFieldValue('shippingProvince', '');
      setFieldValue('shippingPostCode', '');
      setFieldValue('shippingCity', '');
      setFieldValue('shippingStreetAddress', '');
      setFieldValue('shortName', selectedCompany.shortName);
      setFieldValue('incotermCity', selectedCompany.incotermCity);
      setFieldValue('companyWarning', selectedCompany.companyWarning);

      if (documentData.direction === DocumentDirection.INCOMING) {
        const crncy = selectedCompany.supplierCurrencyCode || config.currencyCode;
        const payMethod = selectedCompany.supplierPaymentMethodId || config.defPaymentMethod;
        setFieldValue('currencyCode', preservedCurrencyCode ?? crncy);
        if (preservedExchangeRate !== null && preservedExchangeRate !== undefined) {
          setFieldValue('exchangeRate', preservedExchangeRate);
        }
        setFieldValue('paymentMethodId', payMethod);
        handleCalcDueDate(values.issueDate, values.docDate, payMethod);
        setFieldValue('salesOrPurchasePersonId', selectedCompany.purchasePersonId);
        setFieldValue('deliveryMethodId', selectedCompany.supplierDeliveryMethodId);
        setFieldValue('incotermCode', selectedCompany.supplierIncotermCode);
        setFieldValue('priceListId', preservedPriceListId ?? selectedCompany.supplierPriceListId);
      } else if (documentData.direction === DocumentDirection.OUTGOING) {
        const crncy = selectedCompany.customerCurrencyCode || config.currencyCode;
        const payMethod = selectedCompany.customerPaymentMethodId || config.defPaymentMethod;
        setFieldValue('currencyCode', preservedCurrencyCode ?? crncy);
        if (preservedExchangeRate !== null && preservedExchangeRate !== undefined) {
          setFieldValue('exchangeRate', preservedExchangeRate);
        }
        setFieldValue('paymentMethodId', payMethod);
        handleCalcDueDate(values.issueDate, values.docDate, payMethod);
        setFieldValue('salesOrPurchasePersonId', selectedCompany.salesPersonId);
        setFieldValue('deliveryMethodId', selectedCompany.customerDeliveryMethodId);
        setFieldValue('incotermCode', selectedCompany.customerIncotermCode);
        setFieldValue('priceListId', preservedPriceListId ?? selectedCompany.customerPriceListId);
      } else {
        setFieldValue('currencyCode', preservedCurrencyCode ?? config.currencyCode);
        if (preservedExchangeRate !== null && preservedExchangeRate !== undefined) {
          setFieldValue('exchangeRate', preservedExchangeRate);
        }
      }
    }
  }, [selectedCompany, isCopyMode, isInvoiceModification, values.companyId]);

  useEffect(() => {
    if (Object.keys(values).length === 0) return;
    if (isInvoiceModification) return;

    const currentKey = `${values?.currencyCode ?? ''}|${values?.docDate ? dayjs(values.docDate).format('YYYY-MM-DD') : ''}`;
    const isInitialLoadedState = hasSelectedValues && currentKey === initialLoadedKeyRef.current;
    const hasPreservedCopyRate =
      isCopyMode &&
      copyExchangeRateRef.current !== null &&
      copyExchangeRateRef.current !== undefined &&
      currentKey === initialLoadedKeyRef.current;

    if (lastHandledKeyRef.current === currentKey && lastHandledConfigCurrencyRef.current === config.currencyCode) {
      return;
    }

    if (!initialLoadSkippedRef.current && isInitialLoadedState && !shouldRefreshCopiedExchangeRate) {
      initialLoadSkippedRef.current = true;
      lastHandledKeyRef.current = currentKey;
      lastHandledConfigCurrencyRef.current = config.currencyCode;
      return;
    }

    if (!requiresExchangeRate) {
      setFieldValue('exchangeRate', '');
      lastHandledKeyRef.current = currentKey;
      lastHandledConfigCurrencyRef.current = config.currencyCode;
      preservedCopyRateAppliedRef.current = false;
      return;
    }

    if (hasPreservedCopyRate && !preservedCopyRateAppliedRef.current) {
      setFieldValue('exchangeRate', copyExchangeRateRef.current);
      lastHandledKeyRef.current = currentKey;
      lastHandledConfigCurrencyRef.current = config.currencyCode;
      preservedCopyRateAppliedRef.current = true;
      return;
    }

    if (!values.docDate) {
      lastHandledKeyRef.current = currentKey;
      lastHandledConfigCurrencyRef.current = config.currencyCode;
      setFieldValue('exchangeRate', '');

      if (isNewDocument) {
        return;
      }

      addNotification({
        content: `${dateLabels.docDate} nélkül nem tudjuk az árfolyamot betölteni!`,
        type: NotificationType.ERROR,
      });
      return;
    }

    getDailyRate();

    lastHandledKeyRef.current = currentKey;
    lastHandledConfigCurrencyRef.current = config.currencyCode;
    preservedCopyRateAppliedRef.current = false;
  }, [
    values.currencyCode,
    values.docDate,
    config.currencyCode,
    hasSelectedValues,
    requiresExchangeRate,
    isNewDocument,
    isCopyMode,
    isInvoiceModification,
    shouldRefreshCopiedExchangeRate,
    dateLabels.docDate,
  ]);

  useEffect(() => {
    if (values.projectId) {
      const findedProject = autocompleteData?.project.find((p) => p.projectId === values.projectId);
      handleFieldChange('projectOrderNumber', { value: findedProject?.orderNumber ?? '' });
    } else {
      setFieldValue('projectOrderNumber', '');
    }
  }, [values.projectId, autocompleteData?.project]);

  const handleFieldChange = async (field, newVal) => {
    // Normalize empty-string values to null to avoid wiping defaults
    let selected = newVal?.value ?? null;
    if (selected === '') selected = null;
    await setFieldValue(field, selected);
    await validateField(field);
  };

  const getDefaultSellerBankAccount = (currencyCode) => {
    const bankAccounts = autocompleteData?.bankAccount ?? [];
    return (
      bankAccounts.find((bankAccount) => bankAccount.currencyCode === currencyCode && bankAccount.currencyDefault) ??
      bankAccounts.find((bankAccount) => bankAccount.default) ??
      null
    );
  };

  const setSellerBankAccount = (bankAccount) => {
    setFieldValue('sellerBankAccount', bankAccount?.bankAccountNumber ?? null, false);
    setFieldValue('sellerBankName', bankAccount?.bankName ?? null, false);
  };

  const handleCurrencyChange = async (newVal) => {
    const currencyCode = newVal?.value || null;
    await handleFieldChange('currencyCode', newVal);

    if (documentData.direction === DocumentDirection.OUTGOING) {
      setSellerBankAccount(getDefaultSellerBankAccount(currencyCode));
    }
  };

  useEffect(() => {
    if (
      viewField ||
      isInvoiceModification ||
      documentData.direction !== DocumentDirection.OUTGOING ||
      !(autocompleteData?.bankAccount ?? []).length
    ) {
      return;
    }

    const documentKey = `${selectedValues?.mode ?? 'new'}:${selectedValues?.docId ?? ''}`;
    const sellerBankState = sellerBankStateRef.current;
    const isNewDocumentState = sellerBankState.documentKey !== documentKey;

    if (isNewDocumentState) {
      const selectedCurrencyCode = selectedValues?.currencyCode ?? null;
      if (selectedValues?.docId && values.currencyCode !== selectedCurrencyCode) return;

      sellerBankStateRef.current = { documentKey, currencyCode: values.currencyCode ?? null };
      if (!values.sellerBankAccount) {
        setSellerBankAccount(getDefaultSellerBankAccount(values.currencyCode));
      }
      return;
    }

    if (sellerBankState.currencyCode !== (values.currencyCode ?? null)) {
      sellerBankStateRef.current = { documentKey, currencyCode: values.currencyCode ?? null };
      setSellerBankAccount(getDefaultSellerBankAccount(values.currencyCode));
    }
  }, [
    autocompleteData?.bankAccount,
    documentData.direction,
    selectedValues?.currencyCode,
    selectedValues?.docId,
    selectedValues?.mode,
    values.currencyCode,
    values.sellerBankAccount,
    viewField,
    isInvoiceModification,
  ]);

  const handleProjectFieldChange = async (newVal) => {
    const projectId = newVal?.value ?? null;
    const previousProjectId = values?.projectId ?? null;
    const selectedProject = autocompleteData?.project?.find((project) => project.projectId === projectId) ?? null;
    const projectOrderNumber = selectedProject?.orderNumber ?? '';

    await handleFieldChange('projectId', { value: projectId });

    if (setCompanyFromProject && selectedProject) {
      await setFieldValue('companyId', selectedProject?.companyId ?? null);
      await setFieldValue('companyContactId', selectedProject?.companyContactId ?? null);
    }

    const lines = Array.isArray(values.docLine) ? values.docLine : [];
    if (lines.length === 0 || previousProjectId === projectId || typeof setBulkUpdateDialogState !== 'function') {
      return;
    }

    setBulkUpdateDialogState({
      open: true,
      title: 'Tetelsorok projektje',
      message: 'A fejléc projektje módosult. Frissítsük a tételsorok projekt mezőit is?',
      confirmButtonText: 'Sorok frissítése',
      updates: lines.flatMap((_line, index) => [
        {
          fieldPath: `docLine.${index}.projectId`,
          value: projectId,
          validatePath: `docLine.${index}.projectId`,
        },
        {
          fieldPath: `docLine.${index}.projectOrderNumber`,
          value: projectOrderNumber,
        },
      ]),
      setFieldValue,
      validateField,
    });
  };

  const handleCalcDueDate = async (issueDate, docDate, paymentMethodId, recalculateDeliveryDate = true) => {
    const shouldCalculateDueDate =
      currentDocType?.invoice || Number(docTypeId) === DocumentType.DownPaymentRequest;
    if (currentDocType?.disableDatePrefill || !paymentMethodId || !shouldCalculateDueDate) return;

    const paymentMethod = autocompleteData.paymentMethod.find((method) => method.id === paymentMethodId);
    if (!paymentMethod) return;

    const defDate =
      paymentMethod.paymentDeadlineBasis === PaymentDeadlineBasisType.DATEOFCOMPLETION ? docDate : issueDate;

    if (!defDate) return;

    const calcDate =
      paymentMethod.from === FromType.MONTHEND ? dayjs(defDate).add(1, 'month').startOf('month') : dayjs(defDate);

    const day = Number(paymentMethod.day ?? 0);
    const dueDate = paymentMethod.from === FromType.MONTHEND ? calcDate.add(day - 1, 'day') : calcDate.add(day, 'day');

    await handleFieldChange('dueDate', { value: dueDate });
    if (values.periodicalSettlement && recalculateDeliveryDate) {
      const deliveryDate = calculatePeriodicalDeliveryDate({
        ...values,
        issueDate,
        dueDate,
      });
      if (deliveryDate) await setFieldValue('docDate', deliveryDate);
    }
  };

  const updatePeriodicalDeliveryDate = async (overrides = {}) => {
    if (!values.periodicalSettlement && !overrides.periodicalSettlement) return;
    const deliveryDate = calculatePeriodicalDeliveryDate({ ...values, ...overrides });
    if (deliveryDate) await setFieldValue('docDate', deliveryDate);
  };

  const recalculatePeriodicalDates = async () => {
    const periodStart = dayjs(values.deliveryPeriodStart);
    const periodEnd = dayjs(values.deliveryPeriodEnd);

    if (!values.deliveryPeriodStart || !periodStart.isValid()) {
      addNotification({
        content: 'Add meg az elszámolási időszak kezdetét!',
        type: NotificationType.ERROR,
      });
      return;
    }
    if (!values.deliveryPeriodEnd || !periodEnd.isValid()) {
      addNotification({
        content: 'Add meg az elszámolási időszak végét!',
        type: NotificationType.ERROR,
      });
      return;
    }
    if (periodStart.isAfter(periodEnd, 'day')) {
      addNotification({
        content: 'Az elszámolási időszak kezdete nem lehet későbbi a végénél.',
        type: NotificationType.ERROR,
      });
      return;
    }

    const deliveryDate = calculatePeriodicalDeliveryDate(values);
    if (!deliveryDate) {
      addNotification({
        content: 'A dátumok újraszámításához add meg a kiállítás dátumát és a fizetési határidőt!',
        type: NotificationType.ERROR,
      });
      return;
    }

    await setFieldValue('docDate', deliveryDate, true);
    addNotification({
      content: `A teljesítés dátuma újraszámítva: ${deliveryDate.format('YYYY.MM.DD')}`,
      type: NotificationType.SUCCESS,
    });
  };

  const skipField = (fieldName) => (hidedFieldNames[docTypeId] || []).includes(fieldName);
  const selectedCompanyId = values.companyId ?? selectedValues?.company?.id ?? null;
  const selectedProjectId = values.projectId ?? selectedValues?.project?.projectId ?? null;

  return (
    <>
      <div className="grid grid-cols-6 gap-x-4 gap-y-4 mx-4 mb-2">
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="documentSeriesId"
            component={AutoCompleteSelectUI}
            fullWidth
            disabled={viewField || isLoadedExistingDoc || isInvoiceModification}
            label={<FormLabelUI text="Számkör" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.documentSeriesId} />}
            InputLabelProps={{ shrink: Boolean(values.documentSeriesId) }}
            onChange={(_event, newValue) => handleFieldChange('documentSeriesId', newValue)}
            selectedValue={values.documentSeriesId}
            selectedLabelValue={
              documentSeriesOptions?.find((item) => item.value === values.documentSeriesId)?.label || ''
            }
            dataset={documentSeriesOptions}
          />
        </div>
        {!skipField('docNum') && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="text"
              name="docNum"
              as={InputUI}
              fullWidth
              disabled={viewField || !manualDocNum || isLoadedExistingDoc}
              label={<FormLabelUI text="Bizonylatszám" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.docNum} />}
              InputLabelProps={{ shrink: Boolean(values.docNum) }}
            />
          </div>
        )}

        {!skipField('docStatus') && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="autocomplete"
              name="docStatus"
              component={AutoCompleteSelectUI}
              fullWidth
              disabled={true}
              label={<FormLabelUI text="Státusz" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.docStatus} />}
              InputLabelProps={{ shrink: Boolean(values.docStatus) }}
              onChange={(_e, newVal) => handleFieldChange('docStatus', newVal)}
              selectedValue={values.docStatus}
              selectedLabelValue={getDocStatusLabel(values.docStatus, Boolean(currentDocType?.invoice))}
              dataset={getDocStatusList(Boolean(currentDocType?.invoice))}
            />
          </div>
        )}

        {currentDocType?.useDocumentPriority && !skipField('priorityId') && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="autocomplete"
              name="priorityId"
              component={AutoCompleteSelectUI}
              fullWidth
              disabled={viewField}
              label={<FormLabelUI text="Prioritás" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.priorityId} />}
              InputLabelProps={{ shrink: Boolean(values.priorityId) }}
              onChange={(_event, newValue) => handleFieldChange('priorityId', newValue)}
              selectedValue={values.priorityId}
              selectedLabelValue={selectedPriorityLabel}
              dataset={autocompleteOptions?.documentPiority}
            />
          </div>
        )}

        {!skipField('docDate') && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="date"
              name="docDate"
              component={DatePickerUI}
              value={values.docDate}
              fullWidth
              label={<FormLabelUI text={dateLabels.docDate} />}
              variant="standard"
              helperText={<FormErrorUI message={errors.docDate} />}
              onChange={(date) => {
                handleFieldChange('docDate', { value: date });
                handleCalcDueDate(values.issueDate, date, values.paymentMethodId, false);
                if (
                  isInvoiceModification &&
                  selectedValues?.originalInvoiceDate &&
                  date &&
                  !dayjs(date).isSame(dayjs(selectedValues.originalInvoiceDate), 'month')
                ) {
                  addNotification({
                    content: 'A teljesítési dátum hónapja eltér az eredeti számlától.',
                    type: NotificationType.WARNING,
                  });
                }
              }}
              disabled={
                viewField ||
                isDocDateLocked ||
                (isInvoiceModification && !allowsInvoiceHeaderModification)
              }
              InputLabelProps={{ shrink: Boolean(values.docDate) }}
            />
          </div>
        )}

        {!skipField('issueDate') && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="date"
              name="issueDate"
              component={DatePickerUI}
              value={values.issueDate}
              fullWidth
              label={<FormLabelUI text={dateLabels.issueDate} />}
              variant="standard"
              helperText={<FormErrorUI message={errors.issueDate} />}
              onChange={(date) => {
                handleFieldChange('issueDate', { value: date });
                handleCalcDueDate(date, values.docDate, values.paymentMethodId);
                updatePeriodicalDeliveryDate({ issueDate: date });
              }}
              disabled={
                viewField ||
                (Boolean(currentDocType?.invoice) &&
                  documentData.direction === DocumentDirection.OUTGOING &&
                  !isEditableInvoice)
              }
              InputLabelProps={{ shrink: Boolean(values.issueDate) }}
            />
          </div>
        )}

        {!skipField('dueDate') && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="date"
              name="dueDate"
              component={DatePickerUI}
              value={values.dueDate}
              fullWidth
              label={<FormLabelUI text={dateLabels.dueDate} />}
              variant="standard"
              helperText={<FormErrorUI message={errors.dueDate} />}
              onChange={(date) => {
                handleFieldChange('dueDate', { value: date });
                updatePeriodicalDeliveryDate({ dueDate: date });
              }}
              disabled={viewField || (isInvoiceModification && !allowsInvoiceHeaderModification)}
              InputLabelProps={{ shrink: Boolean(values.dueDate) }}
            />
          </div>
        )}
        {((isDigitalSigningConfigured(config, docTypeId) &&
          isDocumentSigningEnabled(config, docTypeId, currentDocType?.electronicSignatureEnabled)) ||
          usesElectronicInvoiceMethod ||
          values.electronicSignatureRequested) && (
          <div className="col-span-6 md:col-span-2">
            <CheckboxUI
              label={isElectronicInvoiceType ? 'E-számla' : 'E-aláírt bizonylat'}
              checked={Boolean(values.electronicSignatureRequested)}
              disabled={
                viewField ||
                isInvoiceModification ||
                values.docStatus === DocStatus.CANCELLATION ||
                usesElectronicInvoiceMethod ||
                !isDocumentSigningEnabled(config, docTypeId, currentDocType?.electronicSignatureEnabled) ||
                (!isDigitalSigningConfigured(config, docTypeId) && !values.electronicSignatureRequested) ||
                Boolean(
                  !isCopyMode &&
                    (selectedValues?.docId || values.docId) &&
                    selectedValues?.docStatus !== DocStatus.PLANNED
                )
              }
              onCheckedChange={(checked) => setFieldValue('electronicSignatureRequested', checked)}
            />
            <FormErrorUI message={errors.electronicSignatureRequested} />
            {!viewField && !isDigitalSigningConfigured(config, docTypeId) && (
              <div className="text-sm text-gray-500">
                Előbb állítsd be és kapcsold be a digitális aláírást a konfigurációban.
              </div>
            )}
            {values.electronicSignatureRequested && isLoadedExistingDoc && (
              <div className="text-xs text-gray-500">
                {selectedValues?.signedPdfEmailSent
                  ? `E-mailben elküldve${selectedValues.signedPdfEmailSentAt ? `: ${dayjs(selectedValues.signedPdfEmailSentAt).format('YYYY.MM.DD. HH:mm')}` : ''}`
                  : 'E-mailben még nincs elküldve'}
              </div>
            )}
          </div>
        )}
        {supportsPeriodicalSettlement && (
          <>
            <div className="col-span-4 md:col-span-2 flex items-center self-start">
              <Field
                name="periodicalSettlement"
                as={CheckboxUI}
                checked={Boolean(values.periodicalSettlement)}
                label="Folyamatos teljesítésű számla"
                disabled={periodicalFieldsDisabled}
                onCheckedChange={async (checked) => {
                  await setFieldValue('periodicalSettlement', checked);
                  if (!checked) {
                    await setFieldValue('deliveryPeriodStart', null);
                    await setFieldValue('deliveryPeriodEnd', null);
                    return;
                  }
                  await updatePeriodicalDeliveryDate({ periodicalSettlement: true });
                }}
              />
            </div>
            {values.periodicalSettlement && (
              <>
                <div className="col-span-4 md:col-span-1">
                  <Field
                    type="date"
                    name="deliveryPeriodStart"
                    component={DatePickerUI}
                    value={values.deliveryPeriodStart}
                    fullWidth
                    label={<FormLabelUI text="Elszámolási időszak kezdete" />}
                    variant="standard"
                    helperText={<FormErrorUI message={errors.deliveryPeriodStart} />}
                    onChange={(date) => handleFieldChange('deliveryPeriodStart', { value: date })}
                    disabled={periodicalFieldsDisabled}
                    InputLabelProps={{ shrink: Boolean(values.deliveryPeriodStart) }}
                  />
                </div>
                <div className="col-span-4 md:col-span-1">
                  <Field
                    type="date"
                    name="deliveryPeriodEnd"
                    component={DatePickerUI}
                    value={values.deliveryPeriodEnd}
                    fullWidth
                    label={<FormLabelUI text="Elszámolási időszak vége" />}
                    variant="standard"
                    helperText={<FormErrorUI message={errors.deliveryPeriodEnd} />}
                    onChange={(date) => {
                      handleFieldChange('deliveryPeriodEnd', { value: date });
                      updatePeriodicalDeliveryDate({ deliveryPeriodEnd: date });
                    }}
                    disabled={periodicalFieldsDisabled}
                    InputLabelProps={{ shrink: Boolean(values.deliveryPeriodEnd) }}
                  />
                </div>
                <div className="col-span-4 md:col-span-1 flex items-end">
                  <ButtonUI
                    text="Dátumok újraszámítása"
                    type="button"
                    size="sm"
                    className="bg-primary"
                    disabled={periodicalFieldsDisabled}
                    onClick={recalculatePeriodicalDates}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
      <div className="grid grid-cols-4 gap-x-4 gap-y-4 mx-4 mb-2">
        {showCompanyFields && (
          <>
            {!skipField('companyId') && (
              <div className="col-span-4 md:col-span-1">
                <div className="flex items-center">
                  <Field
                    type="autocomplete"
                    name="companyId"
                    component={AutoCompleteSelectUI}
                    fullWidth
                    label={<FormLabelUI text={companyLabel} />}
                    variant="standard"
                    helperText={<FormErrorUI message={errors.companyId} />}
                    onChange={async (_e, newVal) => handleFieldChange('companyId', newVal)}
                    disabled={viewField || isInvoiceModification}
                    InputLabelProps={{ shrink: Boolean(values.companyId) }}
                    selectedValue={values.companyId}
                    selectedLabelValue={
                      autocompleteData.company.find((cm) => cm.id === values.companyId)?.name ||
                      (selectedValues?.company && selectedValues.company.id === values.companyId
                        ? selectedValues.company.name
                        : '')
                    }
                    dataset={autocompleteOptions?.company}
                  />
                  {selectedCompanyId && (
                    <IconButton
                      size="small"
                      sx={{
                        color: '#0076f8',
                        backgroundColor: 'transparent',
                        '&:hover': { backgroundColor: 'transparent' },
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        onCompanyViewOpen?.(selectedCompanyId);
                      }}
                    >
                      <ArrowOutwardIcon fontSize="small" />
                    </IconButton>
                  )}
                  <AddButtonUi onClick={onCompanyAddOpen} disabled={viewField || isInvoiceModification} />
                </div>
              </div>
            )}

            {!skipField('companyCode') && (
              <div className="col-span-4 md:col-span-1">
                <Field
                  type="text"
                  name="companyCode"
                  as={InputUI}
                  fullWidth
                  disabled={true}
                  label={<FormLabelUI text="Partner kód" />}
                  variant="standard"
                  helperText={<FormErrorUI message={errors.companyCode} />}
                  InputLabelProps={{ shrink: Boolean(values.companyCode) }}
                />
              </div>
            )}

            {!skipField('companyName') && (
              <div className="col-span-4 md:col-span-1">
                <Field
                  type="text"
                  name="companyName"
                  as={InputUI}
                  fullWidth
                  disabled={viewField || !isInvoiceModification || !allowsInvoiceHeaderModification}
                  label={<FormLabelUI text="Partner név" />}
                  variant="standard"
                  helperText={<FormErrorUI message={errors.companyName} />}
                  InputLabelProps={{ shrink: Boolean(values.companyName) }}
                />
              </div>
            )}

            {currentDocType?.useBankAccount && !skipField('companyBankId') && (
              <div className="col-span-4 md:col-span-1">
                <Field
                  type="autocomplete"
                  name="companyBankId"
                  component={AutoCompleteSelectUI}
                  fullWidth
                  label={<FormLabelUI text="Bankszámla" />}
                  variant="standard"
                  helperText={<FormErrorUI message={errors.companyBankId} />}
                  onChange={(_e, newVal) => handleFieldChange('companyBankId', newVal)}
                  disabled={viewField || !values.companyId || (isInvoiceModification && !allowsInvoiceHeaderModification)}
                  InputLabelProps={{ shrink: Boolean(values.companyBankId) }}
                  selectedValue={values.companyBankId}
                  selectedLabelValue={selectedCompanyBankLabel}
                  dataset={companyBankOptions}
                />
              </div>
            )}
          </>
        )}

        {!skipField('reference') && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="text"
              name="reference"
              as={InputUI}
              fullWidth
              disabled={viewField || (isInvoiceModification && !allowsInvoiceHeaderModification)}
              label={<FormLabelUI text="Hivatkozási szám" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.reference} />}
              InputLabelProps={{ shrink: Boolean(values.reference) }}
            />
          </div>
        )}

        {currentDocType?.employee && !skipField('employeeId') && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="autocomplete"
              name="employeeId"
              component={AutoCompleteSelectUI}
              fullWidth
              label={<FormLabelUI text="Dolgozó" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.employeeId} />}
              onChange={(_e, newVal) => handleFieldChange('employeeId', newVal)}
              disabled={viewField}
              InputLabelProps={{ shrink: Boolean(values.employeeId) }}
              selectedValue={values.employeeId}
              selectedLabelValue={
                selectedValues?.employee?.name ||
                autocompleteData?.employee?.find((employee) => employee.id === values.employeeId)?.name
              }
              dataset={autocompleteOptions?.employee}
            />
          </div>
        )}
      </div>
      <div className="grid grid-cols-4 gap-x-4 gap-y-4 mx-4 mb-2">
        {!skipField('customStatus') && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="autocomplete"
              name="customStatus"
              component={AutoCompleteSelectUI}
              fullWidth
              disabled={viewField}
              label={<FormLabelUI text="Egyedi státusz" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.customStatus} />}
              InputLabelProps={{ shrink: Boolean(values.customStatus) }}
              onChange={(_e, newVal) => handleFieldChange('customStatus', newVal)}
              selectedValue={values.customStatus}
              selectedLabelValue={selectedCustomStatusLabel}
              dataset={autocompleteOptions?.customStatus}
            />
          </div>
        )}

        {currentDocType?.project && (
          <>
            {!skipField('projectId') && (
              <div className="col-span-4 md:col-span-1">
                <div className="flex items-center">
                  <Field
                    type="autocomplete"
                    name="projectId"
                    component={AutoCompleteSelectUI}
                    fullWidth
                    label={<FormLabelUI text="Projekt" />}
                    variant="standard"
                    helperText={<FormErrorUI message={errors.projectId} />}
                    onChange={(_e, newVal) => handleProjectFieldChange(newVal)}
                    disabled={projectFieldsDisabled}
                    InputLabelProps={{ shrink: Boolean(values.projectId) }}
                    selectedValue={values.projectId}
                    selectedLabelValue={
                      autocompleteData?.project?.find((project) => project.projectId === values.projectId)
                        ?.projectName ||
                      (selectedValues?.project && selectedValues.project.projectId === values.projectId
                        ? selectedValues.project.projectName
                        : '')
                    }
                    dataset={autocompleteOptions?.project}
                  />
                  {selectedProjectId && (
                    <IconButton
                      size="small"
                      sx={{
                        color: '#0076f8',
                        backgroundColor: 'transparent',
                        '&:hover': { backgroundColor: 'transparent' },
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        onProjectViewOpen?.(selectedProjectId);
                      }}
                    >
                      <ArrowOutwardIcon fontSize="small" />
                    </IconButton>
                  )}
                  <AddButtonUi onClick={() => setProjectDialogOpen(true)} disabled={projectFieldsDisabled} />
                </div>
              </div>
            )}

            {!skipField('projectOrderNumber') && (
              <div className="col-span-4 md:col-span-1">
                <Field
                  type="autocomplete"
                  name="projectOrderNumber"
                  component={InputUI}
                  fullWidth
                  label={<FormLabelUI text={getProjectOrderNumberName(params)} />}
                  variant="standard"
                  helperText={<FormErrorUI message={errors.projectOrderNumber} />}
                  disabled={true}
                  InputLabelProps={{ shrink: values.projectOrderNumber !== '' }}
                  value={values.projectOrderNumber}
                />
              </div>
            )}
          </>
        )}

        {!skipField('costCenterCode') && (
          <div className="col-span-4 md:col-span-1">
            <div className="flex items-center">
              <Field
                type="autocomplete"
                name="costCenterCode"
                component={AutoCompleteSelectUI}
                fullWidth
                disabled={projectFieldsDisabled}
                label={<FormLabelUI text="Költséghely" />}
                variant="standard"
                helperText={<FormErrorUI message={errors.costCenterCode} />}
                InputLabelProps={{ shrink: Boolean(values.costCenterCode) }}
                onChange={(_e, newVal) => handleFieldChange('costCenterCode', newVal)}
                selectedValue={values.costCenterCode}
                selectedLabelValue={selectedValues?.costCenter?.name}
                dataset={autocompleteOptions?.costCenter}
              />
              <AddButtonUi onClick={() => setCostCenterDialogOpen(true)} disabled={projectFieldsDisabled} />
            </div>
          </div>
        )}

        {!skipField('paymentMethodId') && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="autocomplete"
              name="paymentMethodId"
              component={AutoCompleteSelectUI}
              fullWidth
              disabled={viewField || (isInvoiceModification && !allowsInvoiceHeaderModification)}
              label={<FormLabelUI text="Fizetési mód" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.paymentMethodId} />}
              InputLabelProps={{ shrink: Boolean(values.paymentMethodId) }}
              onChange={(_e, newVal) => {
                handleFieldChange('paymentMethodId', newVal);
                handleCalcDueDate(values.issueDate, values.docDate, newVal?.value ?? null);
              }}
              selectedValue={values.paymentMethodId}
              selectedLabelValue={selectedValues?.paymentMethod?.name}
              dataset={autocompleteOptions?.paymentMethod}
            />
          </div>
        )}

        {!skipField('currencyCode') && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="autocomplete"
              name="currencyCode"
              component={AutoCompleteSelectUI}
              fullWidth
              disabled={viewField || isInvoiceModification}
              label={<FormLabelUI text="Pénznem" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.currencyCode} />}
              InputLabelProps={{ shrink: Boolean(values.currencyCode) }}
              selectedValue={values.currencyCode}
              selectedLabelValue={autocompleteData?.currency?.find((p) => p.code === values.currencyCode)?.code}
              onChange={(_e, newVal) => handleCurrencyChange(newVal)}
              dataset={autocompleteOptions?.currency}
            />
          </div>
        )}

        {requiresExchangeRate && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="number"
              name="exchangeRate"
              as={InputUI}
              fullWidth
              disabled={viewField || isInvoiceModification}
              label={<FormLabelUI text="Árfolyam" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.exchangeRate} />}
              InputLabelProps={{ shrink: Boolean(values.exchangeRate) }}
            />
          </div>
        )}

        {showCompanyFields &&
          [
            DocumentType.ARInvoiceManual,
            DocumentType.ARInvoice,
            DocumentType.ARInvoiceModification,
            DocumentType.APInvoice,
          ].includes(
            parseInt(docTypeId)
          ) && (
            <div className="pt-3">
              <ButtonUI
                text="Előleg"
                className="bg-primary"
                size="sm"
                disabled={!values.companyId || viewField}
                onClick={() => getDownPaymentDoc(values.companyId)}
              />
            </div>
          )}

        {documentData.enableSettlement && (
          <div className="col-span-4 md:col-span-2">
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Field
                  type="number"
                  name="openAmount"
                  as={InputUI}
                  fullWidth
                  label={<FormLabelUI text="Kintlévőség" />}
                  variant="standard"
                  disabled={true}
                  InputLabelProps={{ shrink: Boolean(values.openAmount) }}
                />
              </div>
              <IconButton
                onClick={() => {
                  if (isCopyMode) return;
                  setSettlementDialogOpen(true);
                }}
                disabled={isCopyMode || (!viewField && !Object.keys(selectedValues).length)}
              >
                <DescriptionIcon />
              </IconButton>
            </div>
          </div>
        )}
        {documentData.additionalCost && (
          <>
            <div className="col-span-4 md:col-span-1">
              <Field
                type="number"
                name="extraCostTotal"
                as={InputUI}
                fullWidth
                disabled={viewField}
                label={<FormLabelUI text="Beszerzési mellékköltség saját pénznemben" />}
                variant="standard"
                helperText={<FormErrorUI message={errors.extraCostTotal} />}
                InputLabelProps={{ shrink: Boolean(values.extraCostTotal) }}
              />
            </div>
            <div className="col-span-4 md:col-span-1">
              <Field
                type="text"
                name="extraCostNote "
                as={InputUI}
                fullWidth
                disabled={viewField}
                label={<FormLabelUI text="Beszerzési mellékköltség leírás" />}
                variant="standard"
                helperText={<FormErrorUI message={errors.extraCostNote} />}
                InputLabelProps={{ shrink: Boolean(values.extraCostNote) }}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default DocumentFields;
